import React, { useMemo } from "react";
import { useSelector } from "react-redux";

import {
  AnalyticsResourceType,
  AnalyzeResourceResponseBodyType,
  MetricArrayType,
} from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import { UseQueryResult } from "@tanstack/react-query";

import { useConfiguration } from "../../api/configurations/use-configuration";
import {
  filterDashboardData,
  getTotalTestVisitors,
  selectData,
} from "../../api/formatters/dashboard";
import { DashboardPageName } from "../../api/types/dashboards";
import { selectedVariations } from "../../store/slices/variations";
import { ExperienceFe } from "../../types/schemas/experiences";

import {
  DashboardGraphConfigs,
  GraphConfig,
  GraphConfigGroup,
} from "./Graphs/Utils/graphConfigs";
import { GroupGraphWrapper } from "./GroupGraphWrapper";
import { SingleGraphWrapper } from "./SingleGraphWrapper";

interface DashboardGraphsProps {
  resourceType: AnalyticsResourceType;
  resource?: ExperienceFe;
  config: DashboardGraphConfigs;
  dashboardData: UseQueryResult<AnalyzeResourceResponseBodyType>[];
  graphMetricTotals?: MetricArrayType;
  metricFilterIndex?: number;
  pageName?: DashboardPageName;
}
export const DashboardGraphs = ({
  resourceType,
  resource,
  config,
  dashboardData,
  graphMetricTotals,
  metricFilterIndex,
  pageName,
}: DashboardGraphsProps) => {
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");
  const variations = useSelector(selectedVariations);

  const rawDatasetData: MetricArrayType = useMemo(() => {
    if (dashboardData) {
      return filterDashboardData(dashboardData, "alwaysOnInterval") ?? [];
    }
    return [];
  }, []);

  const totalTestVisitors = useMemo(() => {
    return getTotalTestVisitors(dashboardData);
  }, [dashboardData]);

  return (
    <Grid
      item
      container
      direction={pageName === "profit" ? "column" : "row"}
      columnSpacing={3}
      rowSpacing={4}
      id={"dashboard-graph-grid"}
    >
      {config.map((_config: GraphConfig | GraphConfigGroup) => {
        // single graph
        if (!_config.isGroup) {
          const config = _config as GraphConfig;
          const data = selectData(
            resourceType,
            // @ts-ignore
            dashboardData,
            config,
            resource,
            storeCurrencyConfig?.value?.locale,
            metricFilterIndex,
            variations
          );
          return (
            <SingleGraphWrapper
              key={config.title[metricFilterIndex || 0]}
              config={config}
              resource={resource}
              resourceType={resourceType}
              data={data}
              rawDatasetData={rawDatasetData}
              totalTestVisitors={totalTestVisitors}
              graphMetricTotals={graphMetricTotals}
              metricFilterIndex={metricFilterIndex}
            />
          );
        } else {
          // multiple graphs
          const config = _config as GraphConfigGroup;
          return (
            <GroupGraphWrapper
              key={config.groupTitle}
              configGroup={config}
              resource={resource}
              resourceType={resourceType}
              dashboardData={dashboardData}
              metricFilterIndex={metricFilterIndex}
              firstConfigData={selectData(
                resourceType,
                // @ts-ignore
                dashboardData,
                config.configs[0],
                resource,
                storeCurrencyConfig?.value?.locale,
                metricFilterIndex,
                variations
              )}
            />
          );
        }
      })}
    </Grid>
  );
};
